import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import ProductCard from '../components/ProductCard';
import EducationModuleCard from '../components/EducationModuleCard';
import { FavoriteButton } from '../system-modules/favorites/components';

export default function ProducerStorefront() {
  const { slug } = useParams();
  const [producer, setProducer] = useState(null);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchProducer = async () => {
      try {
        setLoading(true);

        // Get producer data
        const producerRes = await fetch(`/api/users/${slug}`);
        if (!producerRes.ok) throw new Error('Producer not found');
        const producerData = await producerRes.json();
        setProducer(producerData);

        // Get producer products - using the unified API
        const productsRes = await fetch(`/api/products?producer=${producerData._id}`);
        if (productsRes.ok) {
          setProducts(await productsRes.json());
        } else {
          setProducts([]);
        }
      } catch (err) {
        console.error('Error fetching producer:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    if (slug) fetchProducer();
  }, [slug]);

 const [educationModules, setEducationModules] = useState([]);
 const [loadingEducation, setLoadingEducation] = useState(false);

useEffect(() => {
  const fetchEducationModules = async () => {
    if (!producer || !producer._id) return;
    setLoadingEducation(true);
    try {
      const response = await fetch(`/api/education-modules/storefront/${producer._id}`);
      if (response.ok) {
        const data = await response.json();
        setEducationModules(data);
      }
    } catch (error) {
      console.error('Error fetching education modules:', error);
    } finally {
      setLoadingEducation(false);
    }
  };

  fetchEducationModules();
}, [producer]);

  if (loading) return <div className="text-center py-12">Loading producer store...</div>;
  if (error || !producer) return <div className="text-center py-12 text-red-500">Producer not found</div>;

  const storefront = producer.storefrontV2 || producer.storefront || {};
  const producerName = storefront.name || producer.farmName || producer.name || 'Producer';
  const description = storefront.description || producer.aboutText || '';
  const tagline = storefront.tagline || producer.tagline || '';
  const bannerImage = storefront.bannerImage || producer.heroImageUrl || '';
  const logoUrl = storefront.logoUrl || producer.logoUrl || '';
  const primaryColor = storefront.primaryColor || '#16a34a';
  const teamMembers = storefront.teamMembers || [];

  return (
    <div className="min-h-screen">
      {bannerImage && (
        <div className="relative h-64 bg-gray-200">
          <img src={bannerImage} alt={producerName} className="w-full h-full object-cover" />
        </div>
      )}

      <div className="py-6 px-4" style={{ backgroundColor: primaryColor }}>
        <div className="max-w-6xl mx-auto flex items-center justify-between text-white">
          <div className="flex items-center gap-4">
            {logoUrl ? (
              <img src={logoUrl} alt={producerName} className="w-16 h-16 rounded-full object-cover bg-white p-1" />
            ) : (
              <div className="w-16 h-16 rounded-full bg-white/20 flex items-center justify-center text-2xl">🚜</div>
            )}
            <div>
              <h1 className="text-3xl font-bold">{producerName}</h1>
              {tagline && <p className="text-white/80 text-sm">{tagline}</p>}
              {description && <p className="text-white/60 text-xs mt-1">{description}</p>}
            </div>
          </div>
          <div className="flex items-center gap-4">
            <FavoriteButton vendorId={producer._id} size="md" showTooltip={true} />
            <span className="text-white/80 text-sm">{products.length} products</span>
          </div>
        </div>
      </div>

      {teamMembers.length > 0 && (
        <div className="max-w-6xl mx-auto p-6">
          <h2 className="text-xl font-semibold mb-4">Our Team</h2>
          <div className="flex flex-wrap gap-4">
            {teamMembers.map((member, index) => (
              <div key={index} className="bg-white rounded-lg shadow p-4 text-center min-w-[120px]">
                <div className="w-16 h-16 rounded-full bg-gray-200 mx-auto flex items-center justify-center text-2xl">
                  {member.imageUrl ? (
                    <img src={member.imageUrl} alt={member.name} className="w-full h-full rounded-full object-cover" />
                  ) : '👤'}
                </div>
                <p className="font-medium mt-2">{member.name}</p>
                <p className="text-sm text-gray-500">{member.role}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="max-w-6xl mx-auto p-6">
        {products.length === 0 ? (
          <div className="text-center py-12 bg-gray-50 rounded-lg">
            <p className="text-gray-500">No products available yet.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {products.map(product => (
              <ProductCard key={product._id} product={product} />
            ))}
          </div>
        )}
      </div>

      {/* Education Modules */}
      {!loadingEducation && educationModules.length > 0 && (
        <div className="max-w-6xl mx-auto p-6 pt-0">
          <h2 className="text-2xl font-bold mb-4">📚 Learn About Our Farm</h2>
          <p className="text-gray-600 mb-4">
            Complete our educational modules to learn about our farming practices and earn rewards.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {educationModules.map(module => {
              if (!module || !module.slug || !module.title) {
                console.warn('Skipping invalid module:', module);
                return null;
              }
              return (
                <div key={module._id} className="border rounded-lg overflow-hidden hover:shadow-lg transition bg-white">
                  <div className="p-4">
                    <h3 className="font-semibold text-lg">{module.title}</h3>
                    <p className="text-gray-600 text-sm mt-1 line-clamp-2">{module.description || 'Learn more about our products.'}</p>
                    {module.reward?.creditAmount > 0 && (
                      <div className="mt-2 text-sm text-green-600 font-medium">
                        🎁 Earn {module.reward.creditAmount} credits on completion
                      </div>
                    )}
                    <div className="mt-3 flex items-center justify-between">
                      <span className="text-xs text-gray-400">
                        {module.completions || 0} completions
                      </span>
                      <Link
                        to={`/learn/${module.slug}`}
                        className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 text-sm"
                      >
                        Learn More →
                      </Link>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
